import { useEffect } from 'react';
import type React from 'react';

import { useRouteError } from 'react-router';

import { Redirect } from '@change-corgi/core/react/router';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { buildRedirectErrorUrl, HttpError, RedirectError, UnauthorizedError } from 'src/shared/error';

import { DefaultLayout } from 'src/app/app/layout';
import { ClientPrefetchError } from 'src/app/app/router';
import { useLoginRedirectUrl } from 'src/app/shared/hooks/auth';
import { useQueryString } from 'src/app/shared/hooks/location';

import { ErrorPageContainer } from './ErrorPage';

export const ErrorBoundary: React.FC = () => {
	const error = useRouteError();
	const utilityContext = useUtilityContext();

	useEffect(() => {
		// we want to report those errors only when they're not wrapped inside a ClientPrefetchError
		if (error instanceof HttpError) {
			// HttpError should only be thrown from the API function so that it can be handled at prefetch time instead of at render time
			void utilityContext.errorReporter
				.child({ sampleRate: 0.1 })
				.report(new Error('Unexpected HttpError caught in ErrorBoundary', { cause: error }));
			return;
		}

		const resolvedError = resolveError(error);
		if (resolvedError instanceof HttpError || resolvedError instanceof RedirectError) {
			return;
		}

		if (resolvedError instanceof RedirectError) {
			// RedirectError should only be thrown from the API function so that it can be handled at prefetch time instead of at render time
			void utilityContext.errorReporter
				.child({ sampleRate: 0.1 })
				.report(new Error('Unexpected RedirectError caught in ErrorBoundary', { cause: error }));
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
		void utilityContext.errorReporter.report(resolvedError as any);
	}, [error, utilityContext]);

	const resolvedError = resolveError(error);

	if (resolvedError instanceof RedirectError) {
		return <ErrorBoundaryRedirect error={resolvedError} />;
	}
	if (resolvedError instanceof UnauthorizedError) {
		return <ErrorBoundaryLoginRedirect />;
	}

	return (
		<DefaultLayout>
			<ErrorPageContainer status={resolvedError instanceof HttpError ? resolvedError.code : 500} />
		</DefaultLayout>
	);
};

const ErrorBoundaryRedirect: React.FC<{ error: RedirectError }> = ({ error }) => {
	const query = useQueryString();

	return <Redirect to={buildRedirectErrorUrl(error, query)} permanent={error.permanent} reason={error.reason} />;
};

const ErrorBoundaryLoginRedirect: React.FC = () => {
	const url = useLoginRedirectUrl();

	return <Redirect to={url} />;
};

function resolveError(error: unknown): unknown {
	if (error instanceof ClientPrefetchError && error.cause) {
		return error.cause;
	}
	return error;
}
